import { useCallback, useMemo } from 'react'

import { getIsMobileAppMode, useDataLayerTracking, usePostHogTracking, ReactNativeMessages } from '@scouts/helpers'
import { useSingular } from '@scouts/singular'

import { SubscriptionTiers } from './models'

export const TrackingEvent = {
    // SelfEmployedAnnualSummaryCreate: 'self_employed_annual_summary_create',
    SelfEmployedAnnualSummaryInitiatePayment: 'self_employed_annual_summary_initiate_payment',
    SelfEmployedAnnualSummaryPurchase: 'self_employed_annual_summary_purchase',
    SelfEmployedAnnualSummaryVisitPaywall: 'self_employed_annual_summary_visit_paywall',
    SelfEmployedSubscriptionInitiatePayment: 'self_employed_subscription_initiate_payment',
    SelfEmployedSubscriptionPurchase: 'self_employed_subscription_purchase',
    SelfEmployedSubscriptionVisitPaywall: 'self_employed_subscription_visit_paywall',
    CompanySubscriptionInitiatePayment: 'company_subscription_initiate_payment',
    CompanySubscriptionPurchase: 'company_subscription_purchase',
    CompanySubscriptionVisitPaywall: 'company_subscription_visit_paywall',
    // TaxReturnCreate: 'tax_return_create',
    TaxReturnInitiatePayment: 'tax_return_initiate_payment',
    TaxReturnPurchase: 'tax_return_purchase',
    TaxReturnVisitPaywall: 'tax_return_visit_paywall',
    UserSignedUp: 'user_signed_up',
    SubscriptionStartedNew: 'subscription_started_new',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
type TrackingEvent = (typeof TrackingEvent)[keyof typeof TrackingEvent]

interface UserProperties {
    cookie_utm_campaign?: string | null
    cookie_utm_content?: string | null
    cookie_utm_medium?: string | null
    cookie_utm_source?: string | null
    cookie_utm_term?: string | null
    initial_variant?: string | null
    language_preference?: 'EN' | 'ES' | null
    referral_source?: string | null
}

interface AllEventProperties {
    amount: number | null
    currency: 'EUR'
    hashed_email: string | null
    hashed_phone_number: string | null
    is_mobile_app_mode: boolean | null
    is_returning_customer: boolean | null
    is_new_business: boolean | null
    language_preference?: 'EN' | 'ES' | null
    purchase_id: number | string | null
    referral_source: string | null
    tier: SubscriptionTiers | null
    variant: string | null
}

interface GlobalEventProperties {
    is_mobile_app_mode: AllEventProperties['is_mobile_app_mode']
}

interface SignupEventProperties {
    hashed_email?: AllEventProperties['hashed_email']
    referral_source: AllEventProperties['referral_source']
    language_preference: AllEventProperties['language_preference']
    variant: AllEventProperties['variant']
}

interface SyntheticEventProperties {
    is_synthetic?: boolean
}

interface PurchaseEventProperties {
    amount: AllEventProperties['amount']
    hashed_email?: AllEventProperties['hashed_email']
    hashed_phone_number?: AllEventProperties['hashed_phone_number']
    is_returning_customer?: AllEventProperties['is_returning_customer']
    language_preference?: AllEventProperties['language_preference']
    purchase_id: AllEventProperties['purchase_id']
}

interface SubscriptionEventProperties {
    amount?: AllEventProperties['amount']
    hashed_email?: AllEventProperties['hashed_email']
    hashed_phone_number?: AllEventProperties['hashed_phone_number']
    is_new_business: AllEventProperties['is_new_business']
    is_returning_customer?: AllEventProperties['is_returning_customer']
    language_preference: AllEventProperties['language_preference']
    purchase_id: AllEventProperties['purchase_id']
    tier: AllEventProperties['tier']
    is_synthetic?: SyntheticEventProperties['is_synthetic']
}

type EventProperties = {
    [TrackingEvent.SelfEmployedAnnualSummaryInitiatePayment]: PurchaseEventProperties
    [TrackingEvent.SelfEmployedAnnualSummaryPurchase]: PurchaseEventProperties
    [TrackingEvent.SelfEmployedAnnualSummaryVisitPaywall]: undefined
    [TrackingEvent.SelfEmployedSubscriptionInitiatePayment]: SubscriptionEventProperties
    [TrackingEvent.SelfEmployedSubscriptionPurchase]: SubscriptionEventProperties
    [TrackingEvent.SelfEmployedSubscriptionVisitPaywall]: undefined
    [TrackingEvent.CompanySubscriptionInitiatePayment]: SubscriptionEventProperties
    [TrackingEvent.CompanySubscriptionPurchase]: SubscriptionEventProperties
    [TrackingEvent.CompanySubscriptionVisitPaywall]: undefined
    // [TrackingEvent.TaxReturnCreate]: undefined
    [TrackingEvent.TaxReturnInitiatePayment]: PurchaseEventProperties
    [TrackingEvent.TaxReturnPurchase]: PurchaseEventProperties
    [TrackingEvent.TaxReturnVisitPaywall]: undefined
    [TrackingEvent.UserSignedUp]: SignupEventProperties
    [TrackingEvent.SubscriptionStartedNew]: SubscriptionEventProperties
}

const SUBSCRIPTION_EVENTS = new Set<TrackingEvent>([
    TrackingEvent.SelfEmployedSubscriptionPurchase,
    TrackingEvent.CompanySubscriptionPurchase,
])

// Check if an event is a subscription event
const isSubscriptionEvent = (name: TrackingEvent): name is TrackingEvent => {
    return SUBSCRIPTION_EVENTS.has(name)
}

// Type guard to check if properties are SubscriptionEventProperties
const isSubscriptionEventProperties = (properties: unknown): properties is SubscriptionEventProperties => {
    return (
        typeof properties === 'object' &&
        properties !== null &&
        'is_returning_customer' in properties &&
        typeof (properties as SubscriptionEventProperties).is_returning_customer === 'boolean'
    )
}

const isMobileAppMode = getIsMobileAppMode()

type Capture = {
    <T extends TrackingEvent>(
        name: T,
        properties: EventProperties[T],
        dataLayerOnlyProperties?: Record<string, unknown>
    ): void
    <T extends TrackingEvent>(name: T): void
}

export const useEventTracking = () => {
    const {
        captureEvent: postHogCaptureEvent,
        identifyUser: postHogIdentifyUser,
        reset: postHogResetUser,
        capturePageView: postHogCapturePageView,
    } = usePostHogTracking<TrackingEvent, UserProperties, GlobalEventProperties>()
    const { captureEvent: dataLayerCaptureEvent, identifyUser: dataLayerIdentifyUser } =
        useDataLayerTracking<TrackingEvent>()
    const { track: trackSingularEvent } = useSingular()

    const globalEventProperties = useMemo(
        () => ({
            is_mobile_app_mode: isMobileAppMode,
        }),
        []
    )

    const identify = (id: number, properties: UserProperties) => {
        postHogIdentifyUser(id, properties)
        dataLayerIdentifyUser(id)
    }

    const capturePageView = () => {
        postHogCapturePageView(globalEventProperties)
    }

    const capture: Capture = useCallback(
        <T extends TrackingEvent>(
            name: T,
            properties?: EventProperties[T],
            dataLayerOnlyProperties?: Record<string, unknown>
        ) => {
            /**
             * Do the actual sending of the events to all possible destinations.
             */
            const sendEvent = <T extends TrackingEvent>(
                name: T,
                properties?: EventProperties[T],
                dataLayerOnlyProperties?: Record<string, unknown>
            ) => {
                postHogCaptureEvent(name, { ...properties, ...globalEventProperties })
                dataLayerCaptureEvent(name, {
                    ...properties,
                    ...dataLayerOnlyProperties,
                    ...globalEventProperties,
                })
                ReactNativeMessages.trackingEvent({ name, properties: { ...properties } })

                trackSingularEvent(name, properties)
            }

            /**
             * A synthetic event is an event that is derived from another events based on certain conditions related to the payload
             * of the original event. It should be tracked as a separate event and sent to all destinations as if it were a regular
             * event.
             */
            const captureSyntheticEvent = <T extends TrackingEvent>(
                name: T,
                properties?: EventProperties[T],
                dataLayerOnlyProperties?: Record<string, unknown>
            ) => {
                // This is so that we cut any potential recursive calls to this function.
                // For now, it's not allowed to generate a synthetic event from another synthetic event.
                if (isSubscriptionEventProperties(properties) && properties.is_synthetic) return

                // Mapping rules for "subscription_started_new" event
                if (
                    // It's a subscription event with the correct properties type
                    isSubscriptionEvent(name) &&
                    isSubscriptionEventProperties(properties) &&
                    // It's not a returning customer
                    properties.is_returning_customer === false
                ) {
                    sendEvent(
                        TrackingEvent.SubscriptionStartedNew,
                        {
                            ...properties,
                            is_synthetic: true,
                        },
                        dataLayerOnlyProperties
                    )
                }
            }

            sendEvent(name, properties, dataLayerOnlyProperties)
            captureSyntheticEvent(name, properties, dataLayerOnlyProperties)
        },
        [postHogCaptureEvent, globalEventProperties, dataLayerCaptureEvent, trackSingularEvent]
    )

    return { capture, capturePageView, identify, unidentify: postHogResetUser }
}
